/**
 * Serialize Slate node tree to markdown string.
 */

const serializeLeaf = (node) => {
  let text = node.text;
  if (!text) return '';
  if (node.bold) text = `**${text}**`;
  if (node.italic) text = `*${text}*`;
  if (node.code) text = `\`${text}\``;
  return text;
};

const serializeChildren = (children) => {
  return (children || []).map((child) => {
    if ('text' in child) return serializeLeaf(child);
    return serializeNode(child);
  }).join('');
};

const serializeNode = (node) => {
  if ('text' in node) return serializeLeaf(node);

  const children = serializeChildren(node.children);

  switch (node.type) {
    case 'heading-one':
      return `# ${children}`;
    case 'heading-two':
      return `## ${children}`;
    case 'block-quote':
      return `> ${children}`;
    case 'bulleted-list':
      return (node.children || [])
        .map((item) => `- ${serializeChildren(item.children)}`)
        .join('\n');
    case 'numbered-list':
      return (node.children || [])
        .map((item, i) => `${i + 1}. ${serializeChildren(item.children)}`)
        .join('\n');
    case 'list-item':
      return children;
    case 'link':
      return `[${children}](${node.url || ''})`;
    case 'image':
      return `![${node.alt || ''}](${node.url || ''})`;
    case 'table':
      return serializeTable(node);
    case 'table-row':
    case 'table-cell':
      return children;
    case 'paragraph':
    default:
      return children;
  }
};

const serializeTable = (tableNode) => {
  const rows = tableNode.children || [];
  if (rows.length === 0) return '';

  const lines = rows.map((row) => {
    const cells = (row.children || []).map((cell) => {
      return serializeChildren(cell.children).replace(/\|/g, '\\|').trim() || ' ';
    });
    return `| ${cells.join(' | ')} |`;
  });

  // Insert header separator after first row
  if (lines.length > 0) {
    const colCount = (rows[0].children || []).length;
    const separator = `| ${Array(colCount).fill('---').join(' | ')} |`;
    lines.splice(1, 0, separator);
  }

  return lines.join('\n');
};

export const serializeToMarkdown = (nodes) => {
  return nodes.map((node) => serializeNode(node)).join('\n');
};
